import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  IRetryPolicy,
  LogLevel,
} from '@microsoft/signalr';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import {
  ContextService,
  TokenService,
} from '@maximizer/core/shared/data-access';
import { ActionStatus, RecipeRunCompleted } from '@maximizer/actions/domain';
import { ActionsStore } from '../state/actions.store';
import { ACTIONS_API_URL } from '../tokens';

const RETRY_CAP_MS = 30_000;

const reconnectForever: IRetryPolicy = {
  nextRetryDelayInMilliseconds: ({ previousRetryCount }) =>
    Math.min(RETRY_CAP_MS, 1000 * 2 ** previousRetryCount),
};

interface ActionChangedNotification {
  readonly id: string;
  readonly status: ActionStatus;
}

@Injectable()
export class ActionsRealtimeService implements OnDestroy {
  private readonly store = inject(ActionsStore);
  private readonly context = inject(ContextService);
  private readonly token = inject(TokenService);
  private readonly baseUrl = inject(ACTIONS_API_URL);
  private connection: HubConnection | null = null;
  private retry: ReturnType<typeof setTimeout> | null = null;
  private tokenStale = false;

  private readonly runCompletedSubject = new Subject<RecipeRunCompleted>();

  readonly runCompleted$: Observable<RecipeRunCompleted> =
    this.runCompletedSubject.asObservable();

  connect(): void {
    if (!this.baseUrl || this.connection) {
      return;
    }

    this.connection = new HubConnectionBuilder()
      .withUrl(`${this.baseUrl}/hubs/actions`, {
        accessTokenFactory: () => this.accessToken(),
      })
      .withAutomaticReconnect(reconnectForever)
      .configureLogging(LogLevel.Warning)
      .build();

    this.connection.on(
      'actionChanged',
      (notification: ActionChangedNotification) =>
        this.store.refresh(notification.id),
    );

    this.connection.on('recipeRunCompleted', (run: RecipeRunCompleted) =>
      this.runCompletedSubject.next(run),
    );

    this.connection.onreconnected(() => {
      console.info('[actions] hub reconnected');
      this.store.load();
    });

    this.connection.onreconnecting((error) => {
      this.tokenStale = true;
      console.warn('[actions] hub reconnecting', error);
    });

    this.connection.onclose((error) =>
      console.warn('[actions] hub closed', error),
    );

    const connection = this.connection;

    void connection
      .start()
      .then(() =>
        console.info('[actions] hub connected', `${this.baseUrl}/hubs/actions`),
      )
      .catch((error) => {
        console.error(
          '[actions] hub connection failed',
          `${this.baseUrl}/hubs/actions`,
          error,
        );

        if (this.connection === connection) {
          this.tokenStale = true;
          this.connection = null;
          this.retry = setTimeout(() => {
            this.retry = null;
            this.connect();
          }, RETRY_CAP_MS);
        }
      });
  }

  private accessToken(): Promise<string> {
    if (this.tokenStale) {
      this.tokenStale = false;
      this.context.token = '';
    }

    return firstValueFrom(this.token.getToken());
  }

  disconnect(): void {
    if (this.retry !== null) {
      clearTimeout(this.retry);
      this.retry = null;
    }

    const connection = this.connection;
    this.connection = null;

    if (connection && connection.state !== HubConnectionState.Disconnected) {
      void connection.stop();
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
